import { services } from "@progressive-development/pd-spa-helper";
import { visit } from "@progressive-development/politik-model";

/**
 * Reichweite der Testphase — EIN Aufruf je App-Start.
 *
 * Nicht je Seitenwechsel: Gezählt werden Besucher, nicht Seitenaufrufe. Ein
 * Neuladen feuert erneut, und das ist richtig so — ob daraus eine Zeile wird,
 * entscheidet allein die Function (dieselbe Person zählt einmal am Tag).
 *
 * Der Zähler darf die Seite nie aufhalten und sein Ausfall nie sichtbar sein:
 * kein Store, kein Effect, kein Fehlerpfad, kein `await`. Läuft er nicht,
 * fehlt eine Zahl — mehr passiert nicht.
 *
 * Was hier gebildet wird, ist bewusst GROB. Jeder Wert für sich muss so
 * unscharf sein, dass ihn Millionen teilen; erst die Sammlung feiner Angaben
 * (Bildschirmmaße, Zeitzone, Schriften) wäre ein Fingerabdruck. Browser,
 * System und Adresse liest die Function aus dem Request — der Browser schickt
 * sie NICHT mit.
 */

/** Die drei Klassen kommen aus dem Vertrag, nicht aus dieser Datei. */
export type Geraeteklasse = visit.VisitDevice;

/**
 * Drei Klassen aus einer Media-Query statt aus dem User-Agent.
 *
 * Der Server hätte den User-Agent, aber ihn zu deuten hieße eine Bibliothek
 * pflegen, die jedes Jahr schlechter wird — die Browser frieren den String
 * ein. Ein grober Zeiger sagt „angefasst statt geklickt", die Breite trennt
 * Telefon von Tablet. Die Grenze bei 768 px ist Konvention, keine Wahrheit.
 */
export function geraeteklasse(breite: number, grobzeiger: boolean): Geraeteklasse {
  if (!grobzeiger) return "desktop";
  return breite < 768 ? "phone" : "tablet";
}

/**
 * Der Host, von dem jemand kam — und nur, wenn er fremd ist.
 *
 * Interne Sprünge sind keine Herkunft. Übergeben wird ausschließlich der Host:
 * Pfad und Query einer fremden Seite können Suchbegriffe oder Sitzungsschlüssel
 * enthalten, die sollen gar nicht erst über die Leitung gehen.
 */
export function fremderHost(referrer: string, eigenerHost: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const host = new URL(referrer).host;
    return host && host !== eigenerHost ? host : undefined;
  } catch {
    // Kein gültiger Verweis — dann eben keiner.
    return undefined;
  }
}

/**
 * Der Widerspruch, gerätegebunden.
 *
 * Vorhanden heißt AUS. Wer nichts angefasst hat, hat den Schlüssel nicht —
 * und wird gezählt. Ein Merker für den Normalfall wäre eine Ablage ohne
 * Anlass; abgelegt wird nur die Abweichung.
 *
 * WARUM AUF DEM GERÄT und nicht am Server: Wir erkennen niemanden wieder, das
 * ist der Sinn der Zählung — also kann der Server auch keinen Widerspruch
 * einer Person zuordnen. Der Merker ist die einzige Form, in der ein
 * Widerspruch hier überhaupt wirken kann, und er wirkt stärker als jede
 * serverseitige Sperre: Es wird gar nicht erst gerufen, es verlässt nichts das
 * Gerät. Für einen Opt-out ist der Zugriff auf den Gerätespeicher zudem
 * erforderlich (§ 25 TDDDG) — die Einwilligungsfrage kommt dadurch nicht
 * zurück.
 *
 * Die Grenze, ehrlich: Websitedaten gelöscht, anderer Browser, privates
 * Fenster — dann zählt das Gerät wieder mit. Dagegen hülfe nur
 * Wiedererkennung, also genau das, was hier nicht stattfindet.
 */
export const ZAEHLUNG_AUS_KEY = "app.zaehlung.aus";

export function getCountVisits(): boolean {
  return localStorage.getItem(ZAEHLUNG_AUS_KEY) === null;
}

export function setCountVisits(zaehlen: boolean): void {
  if (zaehlen) {
    localStorage.removeItem(ZAEHLUNG_AUS_KEY);
  } else {
    localStorage.setItem(ZAEHLUNG_AUS_KEY, "1");
  }
}

/**
 * Der Schalter steht in der ADRESSE, nicht auf der Seite.
 *
 * `?zaehlung=aus` schaltet ab, `?zaehlung=an` wieder ein — einmal je Gerät
 * aufgerufen, danach wirkt der Merker. Bewusst ohne sichtbares Bedienelement:
 * Ein Schalter, der auf „an" steht, wirft die Frage auf, warum jemand das
 * vorab entschieden hat. Die Datenschutzseite nennt stattdessen den Weg (kurze
 * Nachricht, Adresse kommt zurück).
 *
 * Unbekannte Werte ändern nichts — der Parameter wird gelesen, nicht gedeutet.
 */
export function zaehlungAusAdresse(suche: string): boolean | undefined {
  const wert = new URLSearchParams(suche).get("zaehlung");
  if (wert === "aus") return false;
  if (wert === "an") return true;
  return undefined;
}

/** Nach dem ersten Rendern, wenn nichts Wichtigeres ansteht. */
function beiGelegenheit(tun: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(tun);
    return;
  }
  // Safari kannte requestIdleCallback lange nicht.
  window.setTimeout(tun, 1);
}

export function besuchMelden(): void {
  // Erst die Adresse: Wer den Schalter gerade umlegt, soll SOFORT wirken —
  // sonst zaehlte der Aufruf, mit dem abgeschaltet wird, noch selbst mit.
  const geschaltet = zaehlungAusAdresse(window.location.search);
  if (geschaltet !== undefined) setCountVisits(geschaltet);

  // Widersprochen? Dann wird nicht gerufen — kein Aufruf, keine Verarbeitung.
  if (!getCountVisits()) return;
  beiGelegenheit(() => {
    void services.functions
      .callFunction(visit.COUNT_VISIT_FUNCTION, {
        device: geraeteklasse(window.innerWidth, window.matchMedia("(pointer: coarse)").matches),
        standalone: window.matchMedia("(display-mode: standalone)").matches,
        referrer: fremderHost(document.referrer, window.location.host),
      })
      .catch(() => {
        // Bewusst still: siehe Kopf.
      });
  });
}
